import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Check, X } from "lucide-react";

/**
 * <QuickAddSelect />
 *
 * Native <select> with an inline "+ Add new…" option at the top that
 * unfolds a mini row (input + save) below the select — no leaving the
 * host dialog. Perfect for the "oops, my dept/category isn't in the
 * list" case where staff otherwise have to cancel, add the missing
 * entry, and come back.
 *
 * Props:
 *   value        — currently-selected value (string)
 *   onChange     — fires with the selected value string
 *   options      — [{ id, name }] or [string]
 *   endpoint     — POST target, e.g. "/asset-categories". Server assigns id.
 *   valueKey     — usually "name" (the field to display + save as the value)
 *   labelKey     — usually "name"
 *   placeholder  — first option copy
 *   onCreated    — optional callback(created) — parent refreshes its list
 *   testidPrefix — for automated tests
 */
const QuickAddSelect = ({
  value,
  onChange,
  options = [],
  endpoint,
  valueKey = "name",
  labelKey = "name",
  payloadKey = "name",
  extraPayload = null,
  placeholder = "— None —",
  onCreated,
  testidPrefix = "quick-add",
  disabled = false,
}) => {
  const [showAdder, setShowAdder] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [justAdded, setJustAdded] = useState(null); // optimistic merge until parent reload lands

  const submit = async () => {
    const name = draft.trim();
    if (!name) return;
    setBusy(true);
    try {
      const payload = { [payloadKey]: name, ...(extraPayload || {}) };
      const { data } = await api.post(endpoint, payload);
      toast.success(`Added "${name}"`);
      const newValue = data?.[valueKey] || name;
      setJustAdded(data || { [valueKey]: name, [labelKey]: name, id: `__new-${name}` });
      onChange(newValue);
      onCreated?.(data);
      setDraft("");
      setShowAdder(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  // Merge justAdded into rendered options (once) so the newly-created row shows
  // immediately, even before the parent list reload lands.
  const mergedOptions = React.useMemo(() => {
    if (!justAdded) return options;
    const hasIt = options.some((o) => {
      const val = typeof o === "string" ? o : o[valueKey];
      return val === (justAdded[valueKey] || justAdded);
    });
    return hasIt ? options : [...options, justAdded];
  }, [options, justAdded, valueKey]);

  return (
    <div className="flex flex-col gap-1.5">
      <select
        data-testid={`${testidPrefix}-select`}
        className="nb-input"
        value={value || ""}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value === "__add_new__") {
            setShowAdder(true);
            return;
          }
          onChange(e.target.value);
        }}
      >
        <option value="">{placeholder}</option>
        <option value="__add_new__" style={{ fontStyle: "italic" }}>
          + Add new…
        </option>
        {mergedOptions.map((o) => {
          const val = typeof o === "string" ? o : o[valueKey];
          const lbl = typeof o === "string" ? o : o[labelKey];
          const key = typeof o === "string" ? o : o.id || val;
          return (
            <option key={key} value={val}>
              {lbl}
            </option>
          );
        })}
      </select>

      {showAdder && (
        <div
          data-testid={`${testidPrefix}-adder`}
          className="flex items-center gap-1.5 border-2 border-dashed border-[#5B3A99] rounded-lg p-1.5 bg-[#F1E7FA]"
        >
          <input
            data-testid={`${testidPrefix}-input`}
            className="nb-input flex-1 py-1 text-sm"
            placeholder="Name for the new entry"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
              if (e.key === "Escape") {
                setShowAdder(false);
                setDraft("");
              }
            }}
          />
          <button
            data-testid={`${testidPrefix}-save`}
            type="button"
            onClick={submit}
            disabled={busy || !draft.trim()}
            className="nb-btn nb-btn-primary p-1.5"
            aria-label="save"
          >
            <Check size={14} strokeWidth={2.5} />
          </button>
          <button
            data-testid={`${testidPrefix}-cancel`}
            type="button"
            onClick={() => {
              setShowAdder(false);
              setDraft("");
            }}
            className="nb-btn bg-white p-1.5"
            aria-label="cancel"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickAddSelect;
export { Plus };
